import React, { useState, useEffect } from "react";
import "./AddTestimonials.scss";

import { projectStorage, projectFirestore } from "../firebase-config/config";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";

import Rating from "@mui/material/Rating";
import StarIcon from "@mui/icons-material/Star";

const AddTestimonials = () => {
  const { user } = useAuthContext();

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    description: "",
    rating: 0,
    status: "active",
  });

  const [mainImage, setMainImage] = useState(null);
  const [overlayImage, setOverlayImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  // New state for error message
  const [error, setError] = useState("");

  // Disable background scroll when modal open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      setError(""); // clear error on modal close
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdd = async () => {
    // Validate name and description are not empty (trimmed)
    if (!formData.name.trim() || !formData.description.trim()) {
      setError("Please fill in Name and Description");
      return;
    }

    setError("");
    setUploading(true);

    try {
      // Add testimonial document first
      const docRef = await projectFirestore.collection("testimonials").add({
        ...formData,
        createdBy: user?.uid || "unknown",
        timestamp: new Date(),
        mainImg: "",
        overlayImg: "",
      });

      const docId = docRef.id;

      // Upload images if any
      const mainImgUrl = mainImage
        ? await uploadImageWithDocId(mainImage, docId, "main")
        : "";

      const overlayImgUrl = overlayImage
        ? await uploadImageWithDocId(overlayImage, docId, "overlay")
        : "";

      // Update testimonial doc with image URLs
      await projectFirestore.collection("testimonials").doc(docId).update({
        mainImg: mainImgUrl,
        overlayImg: overlayImgUrl,
      });

      // Reset form and close modal
      setShowModal(false);
      setFormData({
        name: "",
        role: "",
        description: "",
        rating: 0,
        status: "active",
      });
      setMainImage(null);
      setOverlayImage(null);
    } catch (err) {
      console.error("Error adding testimonial:", err);
      setError("Failed to add testimonial.");
    }

    setUploading(false);
  };

  const uploadImageWithDocId = async (file, docId, type) => {
    try {
      const storageRef = projectStorage.ref(
        `testimonialImages/${docId}/${type}.jpg`
      );
      await storageRef.put(file);
      return await storageRef.getDownloadURL();
    } catch (error) {
      console.error("Image upload failed:", error);
      return "";
    }
  };

  return (
    <div className="add-testimonial-container-parent">
      <button className="open-btn" onClick={() => setShowModal(true)}>
        Add Testimonial
      </button>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add Testimonial</h2>

            {/* Show error message */}
            {error && <p className="error-text">{error}</p>}

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Customer Name"
              required
            />

            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Write your testimonial"
              required
            />

            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleChange}
              placeholder="Customer Role (optional)"
            />

            <div className="star-rating-box">
              <Rating
                name="rating"
                value={formData.rating}
                precision={0.5}
                onChange={(event, newValue) =>
                  setFormData({ ...formData, rating: newValue })
                }
                emptyIcon={
                  <StarIcon style={{ opacity: 0.45 }} fontSize="inherit" />
                }
              />
            </div>
            <p className="rating-text">Rating: {formData.rating || "None"}</p>

            <label htmlFor="main-image" className="image-upload-button">
              Upload Image
            </label>
            <input
              type="file"
              id="main-image"
              accept="image/*"
              onChange={(e) => setMainImage(e.target.files[0])}
            />
            {mainImage && <p>Selected: {mainImage.name}</p>}

            <label htmlFor="overlay-image" className="image-upload-button">
              Upload User Image
            </label>
            <input
              type="file"
              id="overlay-image"
              accept="image/*"
              onChange={(e) => setOverlayImage(e.target.files[0])}
            />
            {overlayImage && <p>Selected: {overlayImage.name}</p>}

            <div className="button-group">
              <button
                className="add-btn"
                onClick={handleAdd}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Add"}
              </button>
              <button
                className="cancel-btn"
                onClick={() => setShowModal(false)}
                disabled={uploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTestimonials;
